import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { bookmarkService } from '../services/bookmarks';
import { authService } from '../services/auth';
import { Bookmark, Component, Project } from '../types';

interface BookmarkContextType {
  bookmarks: Bookmark[];
  isBookmarked: (componentName: string) => boolean;
  addBookmark: (component: Component, project?: Project) => Promise<void>;
  removeBookmark: (componentName: string) => Promise<void>;
  loading: boolean;
  refreshBookmarks: () => Promise<void>;
}

const BookmarkContext = createContext<BookmarkContextType | undefined>(undefined);

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error('useBookmarks must be used within a BookmarkProvider');
  }
  return context;
};

interface BookmarkProviderProps {
  children: ReactNode;
}

export const BookmarkProvider: React.FC<BookmarkProviderProps> = ({ children }) => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);

  // Load bookmarks once when the provider mounts
  useEffect(() => {
    const loadBookmarks = async () => {
      if (authService.isAuthenticated()) {
        try {
          setLoading(true);
          const userBookmarks = await bookmarkService.getBookmarks();
          setBookmarks(userBookmarks);
        } catch (error) {
          console.error('Failed to load bookmarks:', error);
          setBookmarks([]);
        } finally {
          setLoading(false);
        }
      } else {
        setBookmarks([]);
        setLoading(false);
      }
    };

    loadBookmarks();
  }, []);

  // Refresh bookmarks when auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      if (authService.isAuthenticated()) {
        // Reload bookmarks when user signs in
        bookmarkService.getBookmarks().then(setBookmarks);
      } else {
        // Clear bookmarks when user signs out
        setBookmarks([]);
      }
    };

    // Listen for auth changes (you might need to implement this in authService)
    // For now, we'll reload when the component mounts
  }, []);

  // Memoize helper functions to prevent recreation on every render
  const isBookmarked = useCallback((componentName: string): boolean => {
    return bookmarks.some(bookmark => bookmark.componentName === componentName);
  }, [bookmarks]);

  const addBookmark = useCallback(async (component: Component, project?: Project) => {
    try {
      // Ensure we have project ID
      const projectId = project?.id || component.projectId;
      if (!projectId) {
        throw new Error('Component must belong to a project to be bookmarked');
      }

      // Create a temporary bookmark object with minimal data
      const tempBookmark: Bookmark = {
        id: `temp_${Date.now()}`,
        userId: authService.getCurrentUser()?.id || '',
        componentName: component.name,
        projectId: project?.id || component.projectId || ''
      };

      // Add to local state immediately for instant UI update
      setBookmarks(prev => [...prev, tempBookmark]);

      // Actually save the bookmark
      await bookmarkService.addBookmark(component, project);

      // Reload bookmarks to get the real data
      const updatedBookmarks = await bookmarkService.getBookmarks();
      setBookmarks(updatedBookmarks);
    } catch (error) {
      console.error('Failed to add bookmark:', error);
      // Remove the temporary bookmark on error
      setBookmarks(prev => prev.filter(b => b.id !== `temp_${Date.now()}`));
      throw error;
    }
  }, []);

  const removeBookmark = useCallback(async (componentName: string) => {
    try {
      // Remove from local state immediately for instant UI update
      setBookmarks(prev => prev.filter(b => b.componentName !== componentName));

      // Actually remove the bookmark
      await bookmarkService.removeBookmark(componentName);
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
      // Reload bookmarks to restore state on error
      const updatedBookmarks = await bookmarkService.getBookmarks();
      setBookmarks(updatedBookmarks);
      throw error;
    }
  }, []);

  const refreshBookmarks = useCallback(async () => {
    if (authService.isAuthenticated()) {
      try {
        setLoading(true);
        const updatedBookmarks = await bookmarkService.getBookmarks();
        console.log('BookmarkContext: Refreshed bookmarks:', updatedBookmarks);
        setBookmarks(updatedBookmarks);
      } catch (error) {
        console.error('Failed to refresh bookmarks:', error);
        setBookmarks([]);
      } finally {
        setLoading(false);
      }
    } else {
      setBookmarks([]);
      setLoading(false);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value: BookmarkContextType = useMemo(() => ({
    bookmarks,
    isBookmarked,
    addBookmark,
    removeBookmark,
    loading,
    refreshBookmarks
  }), [bookmarks, isBookmarked, addBookmark, removeBookmark, loading, refreshBookmarks]);

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};
